'use client';

import { VoteType } from '@prisma/client';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import React, { useCallback, useMemo, useState } from 'react';
import Banner from '../../../components/banner/Banner';
import { BannerNew } from '../../../components/banner/BannerNew';
import { PhotoWithDetails } from '../page';
import { PhotoViewModal } from './PhotoViewModal';
import { VoteButtons } from './VoteButtons';

const BANNER_POSITION_1 = 4;
const BANNER_POSITION_2 = 8;

const PhotoItem = React.memo(
  ({
    photo,
    onSelect,
    onVote,
    isSelected,
  }: {
    photo: PhotoWithDetails;
    onSelect: (id: string) => void;
    onVote: (photoId: string, voteType: VoteType) => void;
    isSelected: boolean;
  }) => {
    // Мемоизируем колбэк
    const handleVoteWrapper = useCallback(
      (type: VoteType) => {
        onVote(photo.id, type);
      },
      [photo.id, onVote],
    );

    return (
      <motion.div
        layoutId={isSelected ? undefined : photo.id}
        onClick={() => onSelect(photo.id)}
        className={`cursor-pointer aspect-square rounded-lg overflow-hidden relative group bg-slate-900 ${
          isSelected ? 'opacity-0' : 'opacity-100'
        }`}
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
      >
        <Image
          src={photo.imageUrl}
          alt={photo.caption || 'Фото'}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 25vw"
          // blurDataURL можно добавить, если сервер генерирует base64
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-3 flex flex-col justify-end text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div>
            <h3 className="font-semibold truncate text-sm">{photo.caption}</h3>
            <p className="text-xs text-slate-300 truncate">@{photo.author.name}</p>
          </div>
        </div>

        <div
          className="absolute top-2 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover:translate-y-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ✅ ИСПРАВЛЕНО: Передаем только числа и myVote */}
          <VoteButtons
            likes={photo.likes}
            dislikes={photo.dislikes}
            myVote={photo.myVote}
            onVote={handleVoteWrapper}
          />
        </div>
      </motion.div>
    );
  },
);
PhotoItem.displayName = 'PhotoItem';

// --- Main Component ---
export function PhotoGridWithBanners({
  photos,
  initialSelectedPhoto,
  onVote,
  onCommentAdded,
}: {
  photos: PhotoWithDetails[];
  initialSelectedPhoto: PhotoWithDetails | null;
  onVote: (photoId: string, voteType: VoteType) => void;
  onCommentAdded?: (photoId: string, comment: PhotoWithDetails['comments'][number]) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedPhoto?.id || null);

  const handleSelectPhoto = useCallback((id: string) => {
    setSelectedId(id);
    // Можно добавить смену URL (window.history.pushState) для UX
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedId(null);
  }, []);

  // Быстрый поиск фото по ID
  const photosMap = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos]);
  const selectedPhoto = selectedId ? photosMap.get(selectedId) : null;

  // Вставка баннеров (memo, чтобы не пересчитывать при кликах)
  const combinedItems = useMemo(() => {
    const items: (PhotoWithDetails | { type: 'banner'; id: string })[] = [...photos];
    if (items.length >= BANNER_POSITION_2)
      items.splice(BANNER_POSITION_2, 0, { type: 'banner', id: 'banner-2' });
    if (items.length >= BANNER_POSITION_1)
      items.splice(BANNER_POSITION_1, 0, { type: 'banner', id: 'banner-1' });
    return items;
  }, [photos]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 sm:gap-4">
        {combinedItems.map((item) => {
          if ('type' in item && item.type === 'banner') {
            const BannerComponent = item.id === 'banner-1' ? BannerNew : Banner;
            return (
              <div
                key={item.id}
                className="hidden sm:flex aspect-square rounded-lg overflow-hidden items-center justify-center bg-slate-800"
              >
                <BannerComponent />
              </div>
            );
          }

          const photo = item as PhotoWithDetails;
          return (
            <PhotoItem
              key={photo.id}
              photo={photo}
              onSelect={handleSelectPhoto}
              onVote={onVote}
              isSelected={selectedId === photo.id}
            />
          );
        })}
      </div>

      <AnimatePresence>
        {selectedId && selectedPhoto && (
          <PhotoViewModal
            photo={selectedPhoto}
            onClose={handleCloseModal}
            onVote={onVote}
            onCommentAdded={onCommentAdded}
          />
        )}
      </AnimatePresence>
    </>
  );
}
